// @ts-nocheck
import * as React from "react";
import { Logo, Ic, Sidebar, Topbar, LineChart, BarChart, Kpi, AppFooter, useCurrentUser, apiErrorMessage, fmtDuration } from "./common";
import { usePermissionsFromSession } from "../components/permissions";
import { api } from "../api";
import { Modal, Field } from "../components/Modal";
import { RegionCreateModal } from "../components/RegionCreateModal";
import { DeletionConfirmModal } from "../components/DeletionConfirmModal";
import { RegionHistoryModal } from "../components/RegionHistoryModal";
import { RegionMergeModal } from "../components/RegionMergeModal";
import { RowMenu } from "../components/RowMenu";
import { Pager } from "../components/Pager";
import { useSaveToast } from "../components/SaveToast";
import { LocationPicker } from "../components/naver/LocationPicker";

// form(center_latitude/longitude) ↔ LocationPicker(Coord) 어댑터.
const coordOf = (f) =>
  f.center_latitude != null && f.center_longitude != null
    ? { lat: f.center_latitude, lng: f.center_longitude } : null;
// 좌표 미설정 권역의 지도 초기 중심(전국 개관).
const REGION_PICK_CENTER = { lat: 36.5, lng: 127.8 };

const EMPTY_REGION = {
  code: "", name: "", area_description: "", manager_name: "", manager_user_id: "",
  operation_status: "operating", grade: "good",
  monthly_matches: 0, manager_count: 0, org_count: 0, case_count: 0,
  success_rate: 0, avg_response_mins: 0, budget_execution: 0,
  pilot: false,
};

// Fields the operator may set. Stats (monthly_matches, success_rate, …) are
// derived from real activity and intentionally excluded so the UI can't
// poison the dashboards.
const NEW_FIELDS  = ["code", "name", "area_description", "manager_name", "manager_user_id", "operation_status"];
const EDIT_FIELDS = ["name", "area_description", "manager_name", "manager_user_id", "operation_status", "grade", "pilot",
                     "center_latitude", "center_longitude", "default_zoom"];

// Display name for a region's manager: prefer the linked User, fall back
// to the free-text manager_name. "—" when neither is set.
function managerLabel(r) {
  return r?.manager_user?.name || r?.manager_name || "—";
}

function pick(obj, keys) {
  return keys.reduce((acc, k) => { acc[k] = obj[k]; return acc; }, {});
}

// Map raw API error strings to user-facing Korean messages.
// Keeps the modal informative without exposing Rails details.
function humanizeError(e) {
  return apiErrorMessage(e);
}

const GRADE_BADGE = {
  excellent: { label: "우수",     color: "green" },
  good:      { label: "양호",     color: "orange" },
  needs_improvement: { label: "개선필요", color: "rose" },
  pilot:     { label: "시범",     color: "sky" },
};
const OP_BADGE = {
  operating: { label: "운영중", color: "green" },
  pilot:     { label: "시범",   color: "sky" },
  suspended: { label: "중단",   color: "amber" },
  closed:    { label: "폐쇄",   color: "rose"  },
};

// 7개 KPI 메타데이터 — 카드·표·trend pill·정렬이 단일 source에서 동작.
// inverted=true: "값이 작을수록 좋음" (응답시간, 위험 케이스).
// formatNumber/Delta 분리: 비율과 절대 수치의 표시 단위가 다름.
const METRIC_META = {
  monthly_matches:   { label: "이달 매칭",  fmt: v => `${v}`,                   inverted: false },
  success_rate:      { label: "성공률",    fmt: v => `${(v ?? 0).toFixed(0)}%`, fmtDelta: v => `${(v ?? 0).toFixed(1)}%`, inverted: false },
  avg_response_mins: { label: "평균 응답",  fmt: v => fmtDuration(v ?? 0), fmtDelta: v => fmtDuration(v ?? 0), inverted: true  },
  case_count:        { label: "위험 케이스", fmt: v => `${v}`,                   inverted: true  },
  manager_count:     { label: "매니저",    fmt: v => `${v}`,                   inverted: false },
  org_count:         { label: "유관기관",   fmt: v => `${v}`,                   inverted: false },
  budget_execution:  { label: "예산 집행",  fmt: v => `${Math.round((v ?? 0) * 100)}%`, fmtDelta: v => `${Math.round((v ?? 0) * 100)}%p`, inverted: false },
};

// 비교표 컬럼 순서 — 권역 이름 + 7개 KPI. 헤더 클릭 정렬에 그대로 쓰임.
// key는 sort용 정렬자(권역은 이름순), align은 셀 정렬, sortBy는 비교 함수.
const TABLE_COLUMNS = [
  { key: "name",              label: "권역",      align: "left",  sortBy: r => r.name },
  { key: "monthly_matches",   label: "매칭 대상",  align: "right", sortBy: r => r.monthly_matches },
  { key: "success_rate",      label: "성공률",    align: "left",  sortBy: r => r.success_rate },
  { key: "avg_response_mins", label: "평균 응답",  align: "right", sortBy: r => r.avg_response_mins },
  { key: "case_count",        label: "위험 케이스", align: "left",  sortBy: r => r.case_count },
  { key: "manager_count",     label: "매니저 수",   align: "right", sortBy: r => r.manager_count },
  { key: "org_count",         label: "유관기관",    align: "right", sortBy: r => r.org_count },
  { key: "budget_execution",  label: "예산 집행",   align: "left",  sortBy: r => r.budget_execution },
];

// Sort options for the cards/table. Each is a comparator the parent
// applies after filtering, so changing the rule means one entry here.
const SORT_OPTIONS = [
  { key: "code",            label: "코드 순",        cmp: (a,b)=> a.code.localeCompare(b.code) },
  { key: "name",            label: "이름 순",        cmp: (a,b)=> a.name.localeCompare(b.name, 'ko') },
  { key: "matches_desc",    label: "이달 매칭 많은 순", cmp: (a,b)=> b.monthly_matches - a.monthly_matches },
  { key: "success_desc",    label: "성공률 높은 순",   cmp: (a,b)=> b.success_rate - a.success_rate },
  { key: "managers_desc",   label: "매니저 많은 순",   cmp: (a,b)=> b.manager_count - a.manager_count },
];

// Format a timestamp as "방금 전 / N분 전 / N시간 전 / YYYY-MM-DD HH:MM".
// Keep relative for the first day so the operator can tell at a glance
// whether the stats they're reading are fresh.
function formatRefreshedAt(ts) {
  if (!ts) return "아직 집계 안 됨";
  const t = new Date(ts);
  const diffMin = Math.round((Date.now() - t.getTime()) / 60000);
  if (diffMin < 1)    return "방금 전";
  if (diffMin < 60)   return `${diffMin}분 전`;
  if (diffMin < 1440) return `${Math.round(diffMin / 60)}시간 전`;
  return t.toLocaleString("ko-KR", { dateStyle: "short", timeStyle: "short" });
}

// 전권 역할만 임의 권역 대시보드를 ?code=로 열 수 있다(백엔드 resolve_region과 일치).
// 권역 한정 역할은 ?code=가 무시돼 항상 자기 권역이 떠서, 타 권역 카드 클릭은
// "눌러도 같은 화면" 오해를 준다 → 클릭 가능 여부를 이 규칙으로 게이트한다.
const GLOBAL_REGION_ROLES = ["system_admin"];

function RegionManagement() {
  const { user, loaded: userLoaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded: userLoaded });
  // 전권 역할이면 모든 카드, 권역 한정 역할이면 자기 권역 카드만 대시보드로 이동.
  const isGlobalRegionRole = GLOBAL_REGION_ROLES.includes(user?.role);
  const [regions, setRegions] = React.useState([]);
  const [adminUsers, setAdminUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [editing, setEditing] = React.useState(null); // null | "new" | id
  const [form, setForm] = React.useState(EMPTY_REGION);
  const [err, setErr] = React.useState(null);
  const { notifySaved, saveToast } = useSaveToast();
  const [q, setQ] = React.useState("");
  const [opFilter, setOpFilter] = React.useState("all");
  const [sortKey, setSortKey] = React.useState("code");
  // 카드 그리드 페이지네이션. 필터/정렬은 클라이언트라 페이지도 클라이언트 슬라이스.
  // 12 = 3열 × 4행 — 한 화면에 자연스럽게 들어가는 크기.
  const PER_PAGE = 12;
  const [page, setPage] = React.useState(1);
  // 비교표 정렬은 카드 정렬과 독립. 운영자가 카드는 코드순으로 보면서
  // 표는 성공률 desc로 정렬하고 싶을 수 있음.
  const [tableSort, setTableSort] = React.useState({ column: "name", direction: "asc" });
  // null when no deletion is pending; { region, impact, loading } otherwise.
  const [deletePrompt, setDeletePrompt] = React.useState(null);
  // Region whose metric history is currently open in a modal.
  const [historyRegion, setHistoryRegion] = React.useState(null);
  // Region being merged into another. null when no merge in progress.
  const [mergeSource, setMergeSource] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setRegions(await api.list("regions"));
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // 권역 관리자(role=region_admin) 후보를 한 번만 가져와 셀렉트에 채운다.
  // 실패해도 모달은 manager_name 직접 입력으로 동작하므로 치명적이지 않다.
  React.useEffect(() => {
    // 미배정 관리자만 후보로(권역 1:1). 편집 중인 권역의 현재 담당자는 아래
    // managerOptions에서 form.manager_user로 다시 합쳐 표시한다.
    fetch("/api/v1/admins?role=region_admin&available_region_manager=1", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : [])
      .then(list => setAdminUsers(Array.isArray(list) ? list : []))
      .catch(() => setAdminUsers([]));
  }, []);

  // 시범 → 정식 승격. 서버가 자격을 다시 검증하므로 UI는 클릭만 받으면 됨.
  // 422가 떨어지면 사유 배열을 사람 읽기 좋게 합쳐 보여준다.
  const promote = async (r) => {
    if (!confirm(`${r.name} 권역을 정식 운영으로 승격합니다.\n진행할까요?`)) return;
    try {
      const res = await fetch(`/api/v1/regions/${r.id}/promote`, {
        method: "POST", credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { alert(apiErrorMessage(j, res.status)); return; }
      await load();
      notifySaved("승격되었습니다");
    } catch (e) { alert(humanizeError(e)); }
  };

  // Kicks RegionStatsRefreshJob server-side; the response carries the
  // refreshed rows so we update state without a follow-up list call.
  const refreshStats = async () => {
    setRefreshing(true);
    try {
      const r = await fetch("/api/v1/regions/refresh_stats", {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { alert(apiErrorMessage(j, r.status)); return; }
      if (Array.isArray(j.regions)) setRegions(j.regions);
    } catch (e) { alert(humanizeError(e)); } finally { setRefreshing(false); }
  };

  // Use the freshest timestamp across all regions for the header label.
  const lastRefreshedAt = React.useMemo(() => {
    const stamps = regions.map(r => r.stats_refreshed_at).filter(Boolean).map(s => new Date(s).getTime());
    return stamps.length ? new Date(Math.max(...stamps)).toISOString() : null;
  }, [regions]);

  const openNew  = () => { setEditing("new"); };
  const openEdit = (r) => { setForm({ ...r }); setEditing(r.id); };
  const close    = () => { setEditing(null); setErr(null); };

  // 지도 클릭/드래그로 중심 좌표를 직접 지정(아래 숫자 입력란과 form state 공유 → 양방향 동기).
  const setCoord = (c) => setForm(f => ({ ...f, center_latitude: c.lat, center_longitude: c.lng }));

  const save = async () => {
    try {
      const payload = pick(form, editing === "new" ? NEW_FIELDS : EDIT_FIELDS);
      if (editing === "new") await api.create("regions", payload);
      else                    await api.update("regions", editing, payload);
      await load();
      close();
      notifySaved();
    } catch (e) { setErr(humanizeError(e)); }
  };
  // Fetches the impact preview first, then opens a confirm modal that lets
  // the operator see exactly what would be detached before they commit.
  const askDelete = async (r) => {
    setDeletePrompt({ region: r, impact: null, loading: true });
    try {
      const res = await fetch(`/api/v1/regions/${r.id}/deletion_impact`, { credentials: "same-origin" });
      const j = await res.json().catch(() => ({}));
      setDeletePrompt({ region: r, impact: j.impact || {}, loading: false });
    } catch {
      setDeletePrompt({ region: r, impact: {}, loading: false });
    }
  };
  const cancelDelete  = () => setDeletePrompt(null);
  // 모달이 { transferToId } 옵션을 같이 넘긴다. null이면 기존 분리(nullify) 동작.
  // api.remove는 쿼리 파라미터를 지원하지 않아 fetch를 직접 호출한다.
  const confirmDelete = async ({ transferToId } = {}) => {
    if (!deletePrompt) return;
    try {
      const id = deletePrompt.region.id;
      const url = transferToId
        ? `/api/v1/regions/${id}?transfer_to=${transferToId}`
        : `/api/v1/regions/${id}`;
      const res = await fetch(url, { method: "DELETE", credentials: "same-origin" });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(apiErrorMessage(j, res.status));
      }
      setDeletePrompt(null);
      await load();
    } catch (e) { alert(humanizeError(e)); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Apply search + filter + sort once. Cheap (typically <50 rows) so we
  // don't bother memoizing; if region count grows, wrap in useMemo.
  const visible = (() => {
    const needle = q.trim().toLowerCase();
    let out = regions;
    if (needle) {
      out = out.filter(r =>
        (r.code            ||"").toLowerCase().includes(needle) ||
        (r.name            ||"").toLowerCase().includes(needle) ||
        (r.area_description||"").toLowerCase().includes(needle) ||
        (r.manager_name    ||"").toLowerCase().includes(needle)
      );
    }
    if (opFilter !== "all") out = out.filter(r => r.operation_status === opFilter);
    const cmp = SORT_OPTIONS.find(s => s.key === sortKey)?.cmp;
    return cmp ? [...out].sort(cmp) : out;
  })();

  // 필터/정렬 결과에 페이지 슬라이스. 페이지가 visible 범위를 벗어나면 1로 보정.
  const totalPages = Math.max(1, Math.ceil(visible.length / PER_PAGE));
  React.useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);
  // 검색/필터 변경 시 첫 페이지로 자동 복귀. 정렬은 페이지 유지(같은 항목이 그대로).
  React.useEffect(() => { setPage(1); }, [q, opFilter]);
  const pageStart = (page - 1) * PER_PAGE;
  const pageItems = visible.slice(pageStart, pageStart + PER_PAGE);

  // 비교표용 정렬된 행 — filtered + tableSort. 페이지네이션 없음(비교 뷰는 한눈에).
  const tableRows = React.useMemo(() => {
    const col = TABLE_COLUMNS.find(c => c.key === tableSort.column) || TABLE_COLUMNS[0];
    const dir = tableSort.direction === "desc" ? -1 : 1;
    return [...visible].sort((a, b) => {
      const av = col.sortBy(a), bv = col.sortBy(b);
      if (typeof av === "string") return av.localeCompare(bv, "ko") * dir;
      return ((av ?? 0) - (bv ?? 0)) * dir;
    });
  }, [visible, tableSort]);

  // 같은 컬럼 재클릭: 방향 토글. 다른 컬럼 클릭: asc로 시작.
  const toggleTableSort = (key) => setTableSort(s =>
    s.column === key
      ? { column: key, direction: s.direction === "asc" ? "desc" : "asc" }
      : { column: key, direction: "asc" }
  );

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="platform" active="region"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="권역 관리" sub={`전국 ${regions.length}개 운영권역 · DB 연동`}/>
          <div className="main">

            <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:18}}>
              <div>
                <div className="h1">권역 마스터</div>
                <div className="muted" style={{marginTop:4, fontSize:13}}>2026 돌봄통합지원법에 따라 권역별 요양기관협회를 단위로 운영합니다.</div>
              </div>
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                <span className="muted" style={{fontSize:11, marginRight:4}} title={lastRefreshedAt || ""}>
                  통계 갱신: <b style={{color:'var(--ink-2)'}}>{formatRefreshedAt(lastRefreshedAt)}</b>
                </span>
                {perms.canManageRegions && (
                  <button className="btn btn-ghost btn-md" onClick={refreshStats} disabled={refreshing}>
                    {refreshing ? '집계 중…' : '통계 새로고침'}
                  </button>
                )}
                <a className="btn btn-ghost btn-md" href="/map" style={{textDecoration:'none'}}>{Ic.map} 전국 지도</a>
                {perms.canManageRegions && (
                  <button className="btn btn-primary btn-md" onClick={openNew}>{Ic.plus} 권역 신설</button>
                )}
              </div>
            </div>

            {loading && <div className="muted">불러오는 중…</div>}

            <div className="card" style={{padding:'12px 16px', display:'flex', alignItems:'center', gap:10, marginBottom:14, flexWrap:'wrap'}}>
              <div className="searchbar" style={{flex:'0 0 260px'}}>
                {Ic.search}
                <input className="input" placeholder="코드·이름·구역·관리자 검색"
                  value={q} onChange={e=>setQ(e.target.value)}/>
              </div>
              <select className="select" value={opFilter} onChange={e=>setOpFilter(e.target.value)} style={{width:140}}>
                <option value="all">전체 운영상태</option>
                <option value="operating">운영중</option>
                <option value="pilot">시범</option>
                <option value="suspended">중단</option>
                <option value="closed">폐쇄</option>
              </select>
              <select className="select" value={sortKey} onChange={e=>setSortKey(e.target.value)} style={{width:180}}>
                {SORT_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <div style={{flex:1}}/>
              <span className="muted" style={{fontSize:11.5}}>
                {totalPages > 1
                  ? <>페이지 <b>{page}</b> / {totalPages} · {visible.length}건 / 전체 {regions.length}건</>
                  : <>{visible.length}건 / {regions.length}건</>}
              </span>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:14}}>
              {pageItems.map((r,i)=>{
                const gb = GRADE_BADGE[r.grade] || { label: r.grade, color: "gray" };
                const ob = OP_BADGE[r.operation_status] || { label: r.operation_status, color: "gray" };
                const isPilot = r.pilot;
                // 클릭/지도가 실제로 이 권역을 열어주는 경우에만 활성화. 전권 역할은
                // 모든 권역, 권역 한정 역할은 자기 권역만 — 타 권역은 서버가 ?code=/
                // ?region_id=를 무시하고 자기 화면을 띄우므로(대시보드·지도 공통),
                // 카드 클릭과 지도 버튼을 모두 비활성화/숨김 처리한다.
                const cardClickable = isGlobalRegionRole || r.id === user?.region_id;
                return (
                <div key={r.id} className="card"
                  onClick={cardClickable ? ()=>{ window.location.href = `/dashboard/region?code=${encodeURIComponent(r.code)}`; } : undefined}
                  title={cardClickable ? `${r.name} 대시보드 보기` : undefined}
                  style={{overflow:'hidden', cursor: cardClickable ? 'pointer' : 'default', border: isPilot?'1.5px solid var(--orange-500)':'1px solid var(--border-soft)'}}>
                  <div style={{height:120, background: isPilot ? 'linear-gradient(135deg, var(--orange-50), var(--bg-sub))' : 'linear-gradient(135deg, var(--bg-sub), var(--surface))', position:'relative', padding:18, display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                    <div>
                      <div className="mono muted" style={{fontSize:10, fontWeight:700, letterSpacing:'0.08em'}}>{r.code}</div>
                      <div className="h2" style={{fontSize:18, marginTop:2}}>{r.name}</div>
                      <div className="muted" style={{fontSize:11, marginTop:2}}>{r.area_description}</div>
                    </div>
                    <span className={"badge badge-" + ob.color}>{ob.label}</span>
                    <svg width="60" height="60" viewBox="0 0 60 60" style={{position:'absolute', bottom:-12, right:-8, opacity:0.18}}>
                      <path d="M30 4c-9.4 0-17 7.6-17 17 0 13 17 35 17 35s17-22 17-35c0-9.4-7.6-17-17-17Z" fill="var(--orange-500)"/>
                      <circle cx="30" cy="21" r="6" fill="#fff"/>
                    </svg>
                  </div>
                  <div style={{padding:'14px 18px', display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:8, borderBottom:'1px solid var(--border-soft)'}}>
                    <Stat2 l="이달 매칭" v={r.monthly_matches} c="orange"
                      trend={r.trends_30d?.monthly_matches && { delta: r.trends_30d.monthly_matches.delta }}/>
                    <Stat2 l="매니저"   v={r.manager_count}   c="green"
                      trend={r.trends_30d?.manager_count && { delta: r.trends_30d.manager_count.delta }}/>
                    <Stat2 l="성공률"   v={Math.round(r.success_rate)+'%'}
                      c={r.success_rate>=92?'green':r.success_rate>=88?'orange':'rose'}
                      trend={r.trends_30d?.success_rate && {
                        delta: Number(r.trends_30d.success_rate.delta?.toFixed(1)),
                        fmt: v => `${v}%`,
                      }}/>
                  </div>
                  <div style={{padding:'12px 18px', display:'flex', alignItems:'center', gap:8}}>
                    <div className="avatar sm" style={{background: i%2?'var(--green-500)':'var(--orange-500)'}}>{managerLabel(r)[0]}</div>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:11.5, fontWeight:600}}>
                        {managerLabel(r)}
                        {r.manager_user && <span className="badge badge-green" style={{marginLeft:6, fontSize:9, padding:'1px 5px'}}>계정</span>}
                      </div>
                      <div className="muted" style={{fontSize:10}}>
                        {r.manager_user?.email || "권역 관리자"}
                      </div>
                    </div>
                    <span className={"badge badge-" + gb.color}>{gb.label}</span>
                    {r.effective_center && cardClickable && (
                      <a className="btn btn-ghost btn-sm" href={`/map?region_id=${r.id}`}
                         onClick={e=>e.stopPropagation()} style={{textDecoration:'none', padding:'6px 8px'}}
                         aria-label="지도에서 보기"
                         title={`지도에서 보기 (${r.effective_center.lat.toFixed(4)}, ${r.effective_center.lng.toFixed(4)})`}>
                        {Ic.map}
                      </a>
                    )}
                    {perms.canManageRegions && r.operation_status === "pilot" && (
                      <button className="btn btn-sm"
                        onClick={e=>{ e.stopPropagation(); promote(r); }}
                        disabled={!r.promotion?.ready}
                        title={r.promotion?.ready ? "정식 운영으로 승격" : (r.promotion?.reasons || []).join("\n")}
                        style={{
                          background: r.promotion?.ready ? 'var(--green-500)' : 'var(--bg-sub)',
                          color: r.promotion?.ready ? '#fff' : 'var(--ink-3)',
                          cursor: r.promotion?.ready ? 'pointer' : 'not-allowed',
                        }}>
                        정식 승격
                      </button>
                    )}
                    {(() => {
                      // 자주 쓰는 맥락 CTA(정식 승격)·지도는 카드에 두고, 관리·파괴적
                      // 액션은 ⋯ 메뉴로 접어 카드 푸터 폭을 일정하게 유지한다.
                      // 폐쇄 권역은 병합 불가, 다른 활성 권역이 없어도 불가.
                      const hasTarget = regions.some(x => x.id !== r.id && x.operation_status !== "closed");
                      const mergeDisabled = r.operation_status === "closed" || !hasTarget;
                      const menu = [
                        { label: "이력", onClick: () => setHistoryRegion(r), title: "운영 지표 이력 보기" },
                        perms.canManageRegions && {
                          label: "병합", disabled: mergeDisabled, onClick: () => setMergeSource(r),
                          title: mergeDisabled ? "폐쇄 권역이거나 병합할 다른 권역이 없습니다" : "다른 권역으로 흡수" },
                        perms.canManageRegions && { label: "편집", onClick: () => openEdit(r) },
                        perms.canManageRegions && {
                          label: "삭제", danger: true, dividerBefore: true, onClick: () => askDelete(r) },
                      ];
                      // 카드 클릭(권역 대시보드 이동)을 막기 위해 메뉴 상호작용은 stopPropagation.
                      return (
                        <span onClick={e=>e.stopPropagation()} style={{display:'inline-flex'}}>
                          <RowMenu items={menu} placement="up" />
                        </span>
                      );
                    })()}
                  </div>
                </div>
              );})}
            </div>

            {/* Pager — totalPages가 1이면 숨김 */}
            {totalPages > 1 && (
              <Pager page={page} totalPages={totalPages} onPage={setPage}/>
            )}

            {/* Comparison table (read from DB) */}
            <div className="card" style={{marginTop:14, overflow:'hidden'}}>
              <div style={{padding:'16px 22px', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <div className="h3">권역 비교 · 운영 KPI</div>
                {perms.canExportRegions && (
                  <a className="btn btn-ghost btn-sm" href="/api/v1/regions/export.csv" style={{textDecoration:'none'}}>{Ic.arrow} CSV</a>
                )}
              </div>
              <table className="tbl">
                <thead><tr>
                  {TABLE_COLUMNS.map((c, i) => (
                    <SortableTh key={c.key} col={c} sort={tableSort} onToggle={toggleTableSort}
                      style={i === 0 ? { paddingLeft: 22 } : null}/>
                  ))}
                </tr></thead>
                <tbody>
                {tableRows.map(r => (
                  <tr key={r.id}>
                    <td style={{paddingLeft:22}}><b>{r.name}</b></td>
                    <td className="mono" style={{textAlign:'right'}}><CellWithTrend metric="monthly_matches" trend={r.trends_30d?.monthly_matches}>{r.monthly_matches}</CellWithTrend></td>
                    <td>
                      <div style={{display:'flex', alignItems:'center', gap:8}}>
                        <div className="bar" style={{width:80}}><span style={{width:`${r.success_rate}%`, background: r.success_rate>=92?'var(--green-500)':r.success_rate>=88?'var(--orange-500)':'var(--rose-500)'}}/></div>
                        <span className="mono" style={{fontSize:11, fontWeight:700}}>{r.success_rate.toFixed(0)}%</span>
                        <InlineTrend metric="success_rate" trend={r.trends_30d?.success_rate}/>
                      </div>
                    </td>
                    <td className="mono muted" style={{textAlign:'right'}}><CellWithTrend metric="avg_response_mins" trend={r.trends_30d?.avg_response_mins}>{fmtDuration(r.avg_response_mins)}</CellWithTrend></td>
                    <td>
                      <span className={"badge " + (r.case_count>=18?'badge-rose':r.case_count>=12?'badge-amber':'badge-green')}>{r.case_count}</span>
                      <InlineTrend metric="case_count" trend={r.trends_30d?.case_count}/>
                    </td>
                    <td className="mono" style={{textAlign:'right'}}><CellWithTrend metric="manager_count" trend={r.trends_30d?.manager_count}>{r.manager_count}</CellWithTrend></td>
                    <td className="mono" style={{textAlign:'right'}}><CellWithTrend metric="org_count" trend={r.trends_30d?.org_count}>{r.org_count}</CellWithTrend></td>
                    <td>
                      <div style={{display:'flex', alignItems:'center', gap:8}}>
                        <div className="bar green" style={{width:60}}><span style={{width:`${r.budget_execution*100}%`}}/></div>
                        <span className="mono muted" style={{fontSize:11}}>{Math.round(r.budget_execution*100)}%</span>
                        <InlineTrend metric="budget_execution" trend={r.trends_30d?.budget_execution}/>
                      </div>
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>

      {editing === "new" && (
        <RegionCreateModal
          onClose={close}
          onCreated={() => { close(); load(); notifySaved("등록되었습니다"); }}
        />
      )}

      {deletePrompt && (
        <DeletionConfirmModal
          resourceLabel="권역"
          subject={{ code: deletePrompt.region.code, name: deletePrompt.region.name }}
          loading={deletePrompt.loading}
          impact={deletePrompt.impact ? {
            organizations: ["유관기관",   deletePrompt.impact.organizations || 0],
            care_managers: ["케어매니저", deletePrompt.impact.care_managers || 0],
            elderlies:     ["어르신",     deletePrompt.impact.elderlies     || 0],
            cases:         ["케이스",     deletePrompt.impact.cases         || 0],
          } : null}
          transferOptions={regions
            .filter(r => r.id !== deletePrompt.region.id)
            .map(r => ({ id: r.id, label: `${r.code} · ${r.name}` }))}
          onCancel={cancelDelete}
          onConfirm={confirmDelete}
        />
      )}

      {historyRegion && (
        <RegionHistoryModal
          region={historyRegion}
          onClose={()=>setHistoryRegion(null)}
        />
      )}

      {mergeSource && (
        <RegionMergeModal
          source={mergeSource}
          allRegions={regions}
          onClose={()=>setMergeSource(null)}
          onMerged={()=>{ setMergeSource(null); load(); notifySaved("병합되었습니다"); }}
        />
      )}

      <Modal
        open={editing != null && editing !== "new"}
        title={`${form.name || ""} 편집`}
        onClose={close}
        width={560}
        footer={<>
          <button className="btn btn-ghost btn-md" onClick={close}>취소</button>
          <button className="btn btn-primary btn-md" onClick={save}>저장</button>
        </>}
      >
        {err && <div style={{background:'var(--rose-50)', color:'var(--rose-500)', padding:'8px 12px', borderRadius:8, marginBottom:12, fontSize:12}}>{err}</div>}
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
          <Field label={editing === "new" ? "코드 (자동 채번 · 수정 불가)" : "코드 (변경 불가)"}>
            <input className="input mono" value={form.code} readOnly tabIndex={-1}
              placeholder={editing === "new" && !form.code ? "불러오는 중…" : ""}
              style={{background:'var(--bg-sub)', color:'var(--ink-3)', cursor:'not-allowed'}}/>
          </Field>
          <Field label="권역명 *"><input className="input" value={form.name} onChange={e=>set('name')(e.target.value)} required/></Field>
          <Field label="구역 설명"><input className="input" value={form.area_description||''} onChange={e=>set('area_description')(e.target.value)} placeholder="예: 강원 춘천시·홍천군"/></Field>
          <Field label="권역 관리자 계정">
            {(() => {
              // 후보 = 미배정 관리자 + (편집 중인 이 권역의 현재 담당자). 현재 담당자는
              // available 필터에서 빠지므로 form.manager_user로 다시 합쳐 선택 상태를 유지한다.
              const opts = [...adminUsers];
              const cur = form.manager_user;
              if (cur && !opts.some(a => String(a.id) === String(cur.id))) opts.unshift(cur);
              return (
                <select className="select" value={form.manager_user_id || ""}
                  onChange={e => {
                    const id = e.target.value;
                    set('manager_user_id')(id ? Number(id) : null);
                    // 셀렉트 선택 시 manager_name도 자동으로 사용자 이름으로 맞춰둔다(저장 후 백엔드 콜백이 한 번 더 확정).
                    const u = opts.find(a => String(a.id) === id);
                    if (u) set('manager_name')(u.name);
                  }}>
                  <option value="">— 미지정 (직접 입력) —</option>
                  {opts.map(u => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
                </select>
              );
            })()}
          </Field>
          <Field label="관리자 이름 (직접 입력)">
            <input className="input" value={form.manager_name||''}
              onChange={e=>set('manager_name')(e.target.value)}
              placeholder={form.manager_user_id ? "선택한 계정의 이름이 자동 사용됩니다" : "예: 김도현"}
              disabled={!!form.manager_user_id}/>
          </Field>
          <Field label="운영 상태">
            <select className="select" value={form.operation_status} onChange={e=>set('operation_status')(e.target.value)}>
              <option value="operating">운영중</option>
              <option value="pilot">시범</option>
              <option value="suspended">중단 (일시)</option>
              <option value="closed">폐쇄</option>
            </select>
          </Field>
          {editing !== "new" && <Field label="등급">
            <select className="select" value={form.grade} onChange={e=>set('grade')(e.target.value)}>
              <option value="excellent">우수</option>
              <option value="good">양호</option>
              <option value="needs_improvement">개선필요</option>
              <option value="pilot">시범</option>
            </select>
          </Field>}
          {editing !== "new" && <Field label="시범 권역">
            <label style={{display:'flex', alignItems:'center', gap:8, padding:'10px 0'}}>
              <input type="checkbox" checked={!!form.pilot} onChange={e=>set('pilot')(e.target.checked)}/>
              <span style={{fontSize:12}}>시범 운영 중</span>
            </label>
          </Field>}

          {editing !== "new" && <div style={{gridColumn:'1 / span 2'}}>
            <div className="label" style={{fontSize:11, fontWeight:600, color:'var(--ink-2)', marginBottom:4}}>
              권역 중심 좌표 <span className="muted" style={{fontWeight:400, fontSize:10.5, marginLeft:4}}>지도를 클릭하거나 아래 숫자를 직접 입력</span>
            </div>
            <LocationPicker
              value={coordOf(form)}
              onChange={setCoord}
              center={coordOf(form) || form.effective_center || REGION_PICK_CENTER}
              zoom={form.default_zoom || 12}
              height={200}/>
          </div>}

          {editing !== "new" && <Field label="중심 위도 (latitude)">
            <input className="input mono" type="number" step="0.0001" min={-90} max={90}
              value={form.center_latitude ?? ''}
              onChange={e=>set('center_latitude')(e.target.value === '' ? null : Number(e.target.value))}
              placeholder="예: 37.498"/>
          </Field>}
          {editing !== "new" && <Field label="중심 경도 (longitude)">
            <input className="input mono" type="number" step="0.0001" min={-180} max={180}
              value={form.center_longitude ?? ''}
              onChange={e=>set('center_longitude')(e.target.value === '' ? null : Number(e.target.value))}
              placeholder="예: 126.722"/>
          </Field>}
          {editing !== "new" && <Field label="기본 줌 레벨 (1~20)">
            <input className="input mono" type="number" min={1} max={20}
              value={form.default_zoom ?? ''}
              onChange={e=>set('default_zoom')(e.target.value === '' ? null : Number(e.target.value))}
              placeholder="12"/>
          </Field>}
        </div>

        {editing !== "new" && form.effective_center && (
          <div style={{marginTop:10, padding:'10px 14px', background:'var(--bg-sub)', borderRadius:8, fontSize:11.5, color:'var(--ink-2)'}}>
            현재 적용되는 중심점: <b className="mono">{form.effective_center.lat.toFixed(4)}, {form.effective_center.lng.toFixed(4)}</b>
            {!form.center_latitude && <span className="muted" style={{marginLeft:6}}>(하위 리소스 평균에서 유추)</span>}
          </div>
        )}

        {editing !== "new" && (
          <div style={{marginTop:18, padding:'14px 16px', background:'var(--bg-sub)', borderRadius:10}}>
            <div style={{fontSize:11, fontWeight:700, color:'var(--ink-2)', marginBottom:8, letterSpacing:'0.04em'}}>
              운영 통계 (자동 집계 · 편집 불가)
            </div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10, fontSize:12}}>
              <ReadStat l="이달 매칭"  v={form.monthly_matches}/>
              <ReadStat l="성공률"     v={`${(form.success_rate||0).toFixed(1)}%`}/>
              <ReadStat l="평균 응답"  v={fmtDuration(form.avg_response_mins||0)}/>
              <ReadStat l="매니저 수"  v={form.manager_count}/>
              <ReadStat l="유관기관"   v={form.org_count}/>
              <ReadStat l="위험 케이스" v={form.case_count}/>
              <ReadStat l="예산 집행"  v={`${Math.round((form.budget_execution||0)*100)}%`}/>
            </div>
          </div>
        )}
      </Modal>
      {saveToast}
    </div>
  );
}

function Stat2({l,v,c,trend}){
  return (
    <div>
      <div className="muted" style={{fontSize:10, fontWeight:600}}>{l}</div>
      <div style={{fontSize:18, fontWeight:800, color:`var(--${c}-${c==='amber'?'500':'600'})`, fontVariantNumeric:'tabular-nums'}}>{v}</div>
      {trend && <TrendPill {...trend}/>}
    </div>
  );
}

// 30일 대비 delta 배지. inverted=true면 감소가 good(예: 평균 응답시간).
// delta=0이거나 데이터 없을 땐 의도적으로 자리만 차지하지 않고 숨긴다.
function TrendPill({delta, fmt, inverted}){
  if (delta == null || delta === 0) return null;
  const positive = inverted ? delta < 0 : delta > 0;
  const arrow = delta > 0 ? "▲" : "▼";
  const color = positive ? "var(--green-600)" : "var(--rose-500)";
  const shown = Math.abs(typeof delta === 'number' ? (fmt ? fmt(delta) : delta) : delta);
  return (
    <div className="mono" style={{fontSize:9.5, color, fontWeight:700, marginTop:1}}
         title={`30일 전 대비`}>
      {arrow}{shown}
    </div>
  );
}

function ReadStat({l,v}){return(<div><div className="muted" style={{fontSize:10, fontWeight:600}}>{l}</div><div className="mono" style={{fontSize:13, fontWeight:700, color:'var(--ink-1)'}}>{v}</div></div>);}

// 정렬 가능한 <th>. 활성 컬럼에 ▲/▼ 표시, 비활성 컬럼은 hover 시 색상 보강.
function SortableTh({col, sort, onToggle, style}) {
  const active = sort.column === col.key;
  const arrow  = active ? (sort.direction === "asc" ? " ▲" : " ▼") : "";
  return (
    <th
      onClick={() => onToggle(col.key)}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: col.align === "right" ? "right" : "left",
        color: active ? 'var(--ink-1)' : 'var(--ink-2)',
        ...(style || {}),
      }}
      title={`${col.label}로 정렬`}>
      {col.label}{arrow}
    </th>
  );
}

// 비교표 셀: 값 + 같은 줄에 작은 ▲/▼ trend pill. inverted 처리는 METRIC_META 참조.
function CellWithTrend({metric, trend, children}) {
  return (
    <span style={{display:'inline-flex', alignItems:'baseline', gap:6}}>
      {children}
      <InlineTrend metric={metric} trend={trend}/>
    </span>
  );
}

// 표 셀용 인라인 trend. 카드의 TrendPill은 block이라 표 안에서 어색해 별도.
function InlineTrend({metric, trend}) {
  if (!trend || trend.delta == null || trend.delta === 0) return null;
  const meta = METRIC_META[metric] || {};
  const positive = meta.inverted ? trend.delta < 0 : trend.delta > 0;
  const color = positive ? 'var(--green-600)' : 'var(--rose-500)';
  const arrow = trend.delta > 0 ? "▲" : "▼";
  const formatter = meta.fmtDelta || meta.fmt || (v => v);
  const shown = formatter(Math.abs(trend.delta));
  return (
    <span className="mono" style={{fontSize:9.5, color, fontWeight:700}} title="30일 전 대비">
      {arrow}{shown}
    </span>
  );
}



export { RegionManagement };
